import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconCash, IconPlus, IconTrash } from '@tabler/icons-react';
import { paymentCreateSchema, type PaymentCreateInput, type PaymentDto } from '@expeditor/shared';
import { paymentsApi } from '../../shared/api/payments';
import { useAuthStore } from '../../shared/auth/auth.store';

const METHOD_LABEL: Record<string, string> = {
  BANK_TRANSFER: 'Банк. перевод',
  CARD: 'Карта',
  CASH: 'Наличные',
  OTHER: 'Другое',
};

const SOURCE_LABEL: Record<string, { label: string; color: string }> = {
  MANUAL: { label: 'Вручную', color: 'gray' },
  ONEC_PUSH: { label: 'Из 1С', color: 'blue' },
};

interface Props {
  /** Привязываем к одному из этих идентификаторов. */
  shipmentId?: string;
  orderId?: string;
  /** Скрывает форму добавления для клиента. */
  canManage: boolean;
}

/**
 * Секция «Платежи» на карточке перевозки или поручения.
 *
 * Клиент видит таблицу со суммами/статусами (read-only).
 * Менеджер может добавить (modal) или удалить (только MANUAL в течение часа).
 */
export function PaymentsSection({ shipmentId, orderId, canManage }: Props) {
  const me = useAuthStore((s) => s.user);
  const [items, setItems] = useState<PaymentDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);

  // form
  const [amount, setAmount] = useState<number | string>('');
  const [currency, setCurrency] = useState('RUB');
  const [method, setMethod] = useState<PaymentCreateInput['method']>('BANK_TRANSFER');
  const [purpose, setPurpose] = useState('');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [paymentSystemId, setPaymentSystemId] = useState('');
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16));

  const reload = async () => {
    if (!shipmentId && !orderId) return;
    try {
      setError(null);
      const data = shipmentId
        ? await paymentsApi.listForShipment(shipmentId)
        : await paymentsApi.listForOrder(orderId!);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить платежи');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, orderId]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const dto: PaymentCreateInput = {
      shipmentId,
      orderId,
      amount: Number(amount),
      currency: currency.toUpperCase(),
      method,
      purpose,
      counterpartyName: counterpartyName || undefined,
      paymentSystemId: paymentSystemId || undefined,
      occurredAt: new Date(occurredAt).toISOString(),
    };
    const parsed = paymentCreateSchema.safeParse(dto);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return;
    }
    setBusy(true);
    try {
      await paymentsApi.create(parsed.data);
      setOpened(false);
      setAmount('');
      setPurpose('');
      setCounterpartyName('');
      setPaymentSystemId('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось зафиксировать платёж');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Удалить платёж? Возможно только для MANUAL и в течение часа.')) return;
    setBusy(true);
    try {
      await paymentsApi.remove(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  // Сумма по валютам
  const totals = items.reduce<Record<string, number>>((acc, p) => {
    acc[p.currency] = (acc[p.currency] ?? 0) + p.amount;
    return acc;
  }, {});

  void me; // suppress unused

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Title order={4}>Платежи</Title>
        {canManage && (
          <Button leftSection={<IconPlus size={14} />} size="xs" onClick={() => setOpened(true)}>
            Добавить платёж
          </Button>
        )}
      </Group>

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)} mb="sm">
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Text c="dimmed" size="sm">
          Платежей пока нет.
        </Text>
      ) : (
        <>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Дата</Table.Th>
                <Table.Th>Сумма</Table.Th>
                <Table.Th>Способ</Table.Th>
                <Table.Th>Назначение</Table.Th>
                <Table.Th>Корреспондент</Table.Th>
                <Table.Th>Источник</Table.Th>
                {canManage && <Table.Th style={{ width: 60 }} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>{new Date(p.occurredAt).toLocaleString('ru-RU')}</Table.Td>
                  <Table.Td>
                    <Text fw={500}>
                      {p.amount.toLocaleString('ru-RU')} {p.currency}
                    </Text>
                  </Table.Td>
                  <Table.Td>{METHOD_LABEL[p.method] ?? p.method}</Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {p.purpose}
                    </Text>
                  </Table.Td>
                  <Table.Td>{p.counterpartyName ?? '—'}</Table.Td>
                  <Table.Td>
                    <Badge color={SOURCE_LABEL[p.source]?.color ?? 'gray'}>
                      {SOURCE_LABEL[p.source]?.label ?? p.source}
                    </Badge>
                  </Table.Td>
                  {canManage && (
                    <Table.Td>
                      {p.source === 'MANUAL' && (
                        <Tooltip label="Удалить (только в течение часа)">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => onDelete(p.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group mt="sm">
            <IconCash size={16} />
            <Text fw={500}>Итого:</Text>
            {Object.entries(totals).map(([cur, sum]) => (
              <Badge key={cur} size="lg">
                {sum.toLocaleString('ru-RU')} {cur}
              </Badge>
            ))}
          </Group>
        </>
      )}

      <Modal opened={opened} onClose={() => setOpened(false)} title="Новый платёж" size="md">
        <form onSubmit={onCreate}>
          <Stack>
            <Group grow>
              <NumberInput
                label="Сумма"
                required
                value={amount}
                onChange={setAmount}
                min={0}
                decimalScale={2}
                thousandSeparator=" "
              />
              <TextInput
                label="Валюта (ISO)"
                required
                value={currency}
                onChange={(e) => setCurrency(e.currentTarget.value)}
                maxLength={3}
              />
            </Group>
            <Select
              label="Способ"
              required
              data={[
                { value: 'BANK_TRANSFER', label: 'Банковский перевод' },
                { value: 'CARD', label: 'Карта' },
                { value: 'CASH', label: 'Наличные' },
                { value: 'OTHER', label: 'Другое' },
              ]}
              value={method}
              onChange={(v) => v && setMethod(v as PaymentCreateInput['method'])}
            />
            <Textarea
              label="Назначение платежа"
              required
              minRows={2}
              value={purpose}
              onChange={(e) => setPurpose(e.currentTarget.value)}
              placeholder="Оплата по договору D-2026-00001"
            />
            <TextInput
              label="Корреспондент (плательщик/получатель)"
              value={counterpartyName}
              onChange={(e) => setCounterpartyName(e.currentTarget.value)}
            />
            <TextInput
              label="Идентификатор операции в платёжной системе"
              value={paymentSystemId}
              onChange={(e) => setPaymentSystemId(e.currentTarget.value)}
              placeholder="напр. банковский референс"
            />
            <TextInput
              label="Дата операции"
              type="datetime-local"
              required
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setOpened(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={busy}>
                Зафиксировать
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Card>
  );
}
